import React from "react"
import { OpenInNew } from "@mui/icons-material"
import { hasPermission, PERMISSION_LEVELS, type PermissionLevel } from "helpers/permissions"
import { DASHBOARD_EVENTS, type DashboardEventName } from "connected-components/dashboard/analytics"
import {
  beginDuplicateCustomTemplate,
  beginEditCustomTemplate,
  type DuplicateViewFlowActions,
} from "connected-components/asset-view/helpers/duplicate-view-flow"

interface CustomTemplate {
  id: string
  name: string
  permissionLevel: PermissionLevel
}

interface MenuAction {
  id: string
  label?: string
  component?: React.ReactNode
  onClick: () => void | Promise<void>
}

type TrackFn = (event: DashboardEventName, properties?: Record<string, unknown>) => void

export function buildCustomTemplateMenuActions(
  account: string,
  template: CustomTemplate,
  duplicateFlow: DuplicateViewFlowActions,
  deleteTemplate: (template: CustomTemplate) => Promise<void>,
  track: TrackFn,
): MenuAction[] {
  const { setView, setShowMetricsSelector, clearDuplicateState } = duplicateFlow

  const actions: MenuAction[] = [{
    id: "duplicate",
    label: "Duplicate",
    onClick: () => {
      track(DASHBOARD_EVENTS.VIEW_OPENED, { templateId: template.id, templateName: template.name })
      beginDuplicateCustomTemplate({
        viewName: `${template.name} - duplicate`,
        templateId: template.id,
        sourceViewId: template.id,
        duplicateFlow,
      })
    },
  }]

  if (hasPermission(template.permissionLevel, PERMISSION_LEVELS.WRITE)) {
    actions.push({
      id: "edit",
      label: "Edit",
      onClick: () => {
        track(DASHBOARD_EVENTS.VIEW_OPENED, { templateId: template.id, templateName: template.name })
        beginEditCustomTemplate({
          templateId: template.id,
          clearDuplicateState,
          setView,
          setShowMetricsSelector,
        })
      },
    })
  }

  if (hasPermission(template.permissionLevel, PERMISSION_LEVELS.OWNER)) {
    actions.push({
      id: "permissions",
      component: (
        <div className="permissions-menu-item">
          <span>Permissions</span>
          <OpenInNew fontSize="small" />
        </div>
      ),
      onClick: () => {
        track(DASHBOARD_EVENTS.VIEW_PERMISSIONS_CLICKED, { templateId: template.id, templateName: template.name })
        window.open(`/${account}/template/${template.id}/permissions`, "_blank")
      },
    }, {
      id: "delete",
      label: "Delete",
      onClick: async () => {
        await deleteTemplate(template)
        track(DASHBOARD_EVENTS.VIEW_DELETED, { templateId: template.id, templateName: template.name })
        setView("exposure")
      },
    })
  }

  return actions
}
